import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const BLUE = '#2563eb';
const RED = '#dc2626';
const selectStyle = { height: 34, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 0.5rem', fontSize: '0.85rem', background: 'white' };

const ChartTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div data-testid="finance-chart-tooltip" style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem 0.7rem', fontSize: '0.8rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <p style={{ fontWeight: 600, color: '#2D3748', marginBottom: 2 }}>Dia {d.day}</p>
      <p style={{ color: BLUE }}><strong>{d.assinaturas} {d.assinaturas === 1 ? 'assinatura' : 'assinaturas'}</strong> / {brl(d.valor)}</p>
      <p style={{ color: RED }}>{d.cancelamentos} {d.cancelamentos === 1 ? 'cancelamento' : 'cancelamentos'}</p>
    </div>
  );
};

const MonthNav = ({ ym, setYm }) => {
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: thisYear - 2023 + 2 }, (_, i) => 2023 + i);
  const shift = (n) => { const d = new Date(ym.y, ym.m - 1 + n, 1); setYm({ y: d.getFullYear(), m: d.getMonth() + 1 }); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="finance-chart-prev"><ChevronLeft className="w-4 h-4" /></Button>
      <select value={ym.m} onChange={(e) => setYm({ ...ym, m: Number(e.target.value) })} style={selectStyle} data-testid="finance-chart-month">
        {MONTHS.map((l, i) => <option key={l} value={i + 1}>{l}</option>)}
      </select>
      <select value={ym.y} onChange={(e) => setYm({ ...ym, y: Number(e.target.value) })} style={selectStyle} data-testid="finance-chart-year">
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="finance-chart-next"><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );
};

export const FinanceChart = ({ headers, card }) => {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/digital/finance/chart`, { headers, params: { year: ym.y, month: ym.m } }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [ym]); // eslint-disable-line
  return (
    <div style={card} data-testid="finance-chart-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: '0.75rem' }}>
        <h3 style={{ fontWeight: 600, color: '#2D3748' }}>Assinaturas/Cancelamentos por dia</h3>
        <MonthNav ym={ym} setYm={setYm} />
      </div>
      {data && (
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.85rem', marginBottom: 8 }} data-testid="finance-chart-totals">
          <span style={{ color: '#4a5568', fontWeight: 600 }}>Somatório mensal:</span>
          <span style={{ color: BLUE }}>Assinaturas: <strong>{data.totals.assinaturas}</strong></span>
          <span style={{ color: BLUE }}>Valor total: <strong>{brl(data.totals.valor)}</strong></span>
          <span style={{ color: RED }}>Cancelamentos: <strong>{data.totals.cancelamentos}</strong></span>
        </div>
      )}
      <div style={{ height: 300 }}>
        {data === null ? <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader2 className="w-6 h-6 animate-spin" style={{ color: BLUE }} /></div>
          : data === false ? <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>Não foi possível carregar o gráfico.</p> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.days} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} label={{ value: 'Dia do mês', position: 'insideBottom', offset: -2, fontSize: 11 }} height={36} />
                <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="count" type="monotone" dataKey="assinaturas" name="Assinaturas" stroke={BLUE} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                <Line yAxisId="count" type="monotone" dataKey="cancelamentos" name="Cancelamentos" stroke={RED} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
      </div>
    </div>
  );
};
